import React, { useState, useEffect, useMemo } from "react";
import { useLocation, Link } from "react-router-dom";
import {
  GitCompareArrows,
  Home,
  CloudUpload,
  Link2,
  FileText,
  FileCog,
  FileOutput,
  Workflow,
  Shield,
  Users,
  MonitorSmartphone,
  Settings,
  LayoutDashboard,
  BookOpen,
  BookText, // Glossário (25/09/2026)
  Trash2,
  Eye,
  Monitor,
  Server,
  Building2,
  MapPin,
  AlertTriangle,
  Database,
  BarChart3,
  HardDrive,
  Zap,
  Users2,
  GitBranch,
  Handshake,
  Store,
  Activity,
  Rss,
  Map,
  ChevronDown,
  ChevronRight,
  SidebarClose,
  SidebarOpen,
  Menu,
  ArrowRightLeft,
  LogOut,
  ListChecks,
  FileSearch,
  List,
  Package,
  Plus,
  PlusCircle,
  Terminal,
  CreditCard, // Novo ícone para Terminais de Caixa
  TrendingUp, // Importação necessária para o item
  Lock,
  Key,
  KeyRound, // Ícone para Cofre Técnico
  Layers, // Ícone para Lotes de Licença
  Bell, // Ícone para Alertas
  Mail, // Ícone para Provedores de Comunicação
  Inbox, // Ícone para Centro de Notificações
  Ticket, // Ícone para Chamados (PRD 1, Story 1.1.5)
  KanbanSquare, // Ícone para Tarefas Kanban (PRD 1, Story 1.4.3)
  FolderKanban, // Ícone para Projetos (PRD 1, Story 1.4.5)
  Network, // Ícone para Raio-X do Sistema
  Code2, // Ícone para APIs
  Webhook, // Ícone para Webhooks
  FileCode, // Ícone para Catálogo de APIs
  ScrollText, // Ícone para Logs
  Upload, // Ícone para Import Wizard
  ArrowLeftRight, // Ícone para Migração Bitrix24
  ExternalLink, // Ícone para Portal do Parceiro
  BarChart2, // Ícone para Dashboard de Comunicação
  FileEdit, // Ícone para Templates
  Cog, // Ícone para Regras de Automação
  SlidersHorizontal, // Ícone para Preferências
  Palette, // Ícone para Aparência/Temas
  FileBarChart, // Ícone para Relatórios
  ClipboardCheck, // Ícone para Conferência (Lotese)
  ShieldAlert, // Ícone para LGPD DPO Dashboard
  DoorOpen, // Ícone para Salas (E13)
  CalendarDays, // Ícone para Calendar (Expansão v2, Story E2.3.1)
  FileSpreadsheet, // Ícone para Produção do REL103 (E19)
  ScanFace, // Ícone para Credenciais AWS da biometria
} from "lucide-react";
import {
  Bot,
  Construction,
  MessageCircle,
  Volume2,
  UsersRound,
  Megaphone,
  Target,
  PenSquare,
  Video,
  Film,
  Phone,
  ShieldCheck,
  CalendarHeart,
  Route,
  CalendarOff,
} from "lucide-react";
import { rbacService } from "@/services/rbacService";
import { hasPermission as checkPermission, parsePermissionsFromAPI } from "@/lib/permissions";
import { useAuth } from "@/contexts/AuthContext";
import { useMaintenanceContext } from "@/contexts/MaintenanceContext";
import { UnreadBadge } from "@/components/chat/UnreadBadge";
import { useNaoLidasDoChat } from "@/hooks/useNaoLidasDoChat";
import { useNovidadesDoFeed } from "@/hooks/useNovidadesDoFeed";

interface SidebarItem {
  icon: React.ReactNode;
  label: string;
  path: string;
  children?: SidebarItem[];
  badge?: string;
  /** Número vivo ao lado do rótulo: as mensagens não lidas do chat ou os posts novos do feed. */
  contador?: "chat" | "feed";
  /** Optional permission requirement - format: "resource:action" (e.g., "substabelecidos:manage") */
  requiredPermission?: string;
  /** If true, opens in new tab instead of navigating via router */
  external?: boolean;
}

/** Maps top-level sidebar paths to maintenance module IDs */
const PATH_TO_MODULE_ID: Record<string, string> = {
  "/rh": "rh",
  "/lojas": "lojas",
  "/terminais": "terminais",
  "/maquinas": "maquinas",
  "/vpn": "vpn",
  "/documentos": "documentos",
  "/drive": "documentos",
  "/sinistros": "sinistros",
  "/substabelecidos": "substabelecidos",
  "/alerts": "alertas",
  "/workflows": "workflows",
  "/webhooks": "webhooks",
  "/apis": "apis",
  "/seguranca": "seguranca",
  "/infraestrutura": "infraestrutura",
  "/onboarding": "onboarding",
  "/admin": "admin",
  "/relatorios": "relatorios",
  "/notificacoes": "notificacoes",
  "/aparencia": "aparencia",
  "/tickets": "tickets",
  "/tasks": "tasks",
  "/projects": "projects",
  "/cofre": "cofre",
  "/cofre/contas-de-compra": "cofre",
  "/cofre/licencas": "cofre",
  "/cofre/lotes": "cofre",
  "/cofre/fornecedores": "cofre",
  "/cofre/credenciais": "cofre",
  "/cofre/off-boarding": "cofre",
  "/filial": "filial-profile",
  "/admin/migration": "migration_bitrix",
  "/salas": "salas",
  // Motor de Publicação — Story 3.1. Chave RASA de propósito: o lookup usa
  // `pathPrefix = "/" + item.path.split("/")[1]`, então uma chave profunda
  // ("/publicacoes/ocasioes") nunca seria consultada — vira código morto.
  "/publicacoes": "publicacoes",
  "/admin/audit-events": "audit_events",
  "/admin/rbac-audit": "admin_rbac",
  "/admin/home-widgets": "admin_home_widgets",
  // Inerte para filho de menu (o lookup é só no nível 0, por prefixo); fica pelo padrão
  // das outras subpáginas de Administração.
  "/admin/roteiro": "roteiro",
  "/calendar": "calendar",
  "/process-designer": "process_designer",
  "/feed-global": "feed",
  "/central": "central",
  "/central/gestao": "central",
  "/central/documentos": "central",
  "/central/artigos": "central",
  "/central/painel": "central",
  "/central/templates": "central",
  "/central/remessas": "central",
  "/metas": "metas",
  // Figurinhas (Task 7) -- so a entrada de manutencao. O item de menu
  // fica pra Parte 2, junto da pagina /figurinhas (CLAUDE.md proibe
  // item que leva a pagina vazia).
  "/figurinhas": "stickers",
};

const sidebarItems: SidebarItem[] = [
  {
    icon: <Home className="h-4 w-4" />,
    label: "Início",
    path: "/",
  },
  // ⚠️ Aqui ficava "Dashboard" (/dashboard): números fixos ("1,234
  //    colaboradores"), atividades escritas à mão e gráficos "a implementar",
  //    visível para todos. Saiu em 14/09/2026, antes da campanha de lançamento.
  //    Só volta com dado real — ver Sidebar.sem_dashboard_inventado.test.tsx.
  // 🔑 SEM `requiredPermission`: o organograma é da empresa, não do RH. Fica no
  //    nível de cima justamente para não parecer funcionalidade de um setor.
  //    Quem EDITA continua entrando por RH › Visualização em Árvore, que exige
  //    `rh:organograma_view`.
  {
    icon: <Network className="h-4 w-4" />,
    label: "Organograma",
    path: "/organograma",
  },
  {
    icon: <BarChart3 className="h-4 w-4" />,
    label: "Relatórios e Dashboards",
    path: "/relatorios/kpis",
    requiredPermission: "relatorios:kpis_view",
    children: [
      {
        icon: <LayoutDashboard className="h-3 w-3" />,
        label: "Dashboard KPIs",
        path: "/relatorios/kpis",
        requiredPermission: "relatorios:kpis_view",
      },
      {
        icon: <ListChecks className="h-3 w-3" />,
        label: "Compliance e SLA",
        path: "/relatorios/compliance-sla",
        requiredPermission: "relatorios:compliance_view",
      },
      {
        icon: <TrendingUp className="h-3 w-3" />,
        label: "Painel Executivo",
        path: "/relatorios/executivo",
        requiredPermission: "relatorios:executivo_view",
      },
      {
        icon: <FileText className="h-3 w-3" />,
        label: "Indicadores Documentais",
        path: "/relatorios/documentais",
        requiredPermission: "relatorios:documentais_view",
      },
      {
        icon: <List className="h-3 w-3" />,
        label: "Lista Customizada",
        path: "/relatorios/customizados",
        requiredPermission: "relatorios:customizados_view",
      },
    ],
  },
  {
    icon: <CreditCard className="h-4 w-4" />,
    label: "Terminais de Caixa",
    path: "/terminais/lista",
    requiredPermission: "terminais_lista:view",
    children: [
      {
        icon: <ListChecks className="h-3 w-3" />,
        label: "Lista de Terminais",
        path: "/terminais/lista",
        requiredPermission: "terminais_lista:view",
      },
      {
        icon: <PlusCircle className="h-3 w-3" />,
        label: "Cadastrar Terminal",
        path: "/terminais/cadastro",
        requiredPermission: "terminais_cadastro:manage",
      },
      {
        // A produção mensal do Banese e a comissão que sai dela (E19).
        // Sem esta entrada as telas existem e ninguém as alcança.
        icon: <FileSpreadsheet className="h-3 w-3" />,
        label: "Produção e Comissão",
        path: "/terminais/producao",
        requiredPermission: "terminais_lista:view",
      },
      {
        icon: <BarChart3 className="h-3 w-3" />,
        label: "Dashboard Periféricos",
        path: "/terminais/dashboard-perifericos",
        requiredPermission: "terminais_dashboard:view",
      },
      {
        icon: <AlertTriangle className="h-3 w-3" />,
        label: "Controle de Sinistros",
        path: "/terminais/sinistros",
        requiredPermission: "terminais_sinistros:view",
      },
      {
        icon: <Activity className="h-3 w-3" />,
        label: "Logs de Ocorrências",
        path: "/terminais/logs-ocorrencias",
        requiredPermission: "terminais_lista:view",
      },
      {
        icon: <FileSearch className="h-3 w-3" />,
        label: "Auditoria",
        path: "/terminais/auditoria",
        requiredPermission: "terminais_auditoria:view",
      },
    ],
  },
  {
    icon: <Bell className="h-4 w-4" />,
    label: "Alertas",
    path: "/alerts",
    requiredPermission: "alertas:view",
  },
  {
    icon: <MessageCircle className="h-4 w-4" />,
    label: "Comunicação",
    path: "/mensagens",
    requiredPermission: "chat:read",
    contador: "chat",
    children: [
      {
        icon: <MessageCircle className="h-3 w-3" />,
        label: "Mensagens / Chat",
        path: "/mensagens",
        requiredPermission: "chat:read",
        contador: "chat",
      },
      {
        icon: <Volume2 className="h-3 w-3" />,
        label: "Canais de Voz",
        path: "/voz",
        requiredPermission: "voice_channels:read",
      },
      {
        icon: <UsersRound className="h-3 w-3" />,
        label: "Huddles",
        path: "/huddle",
        requiredPermission: "huddle:read",
      },
      {
        icon: <ClipboardCheck className="h-3 w-3" />,
        label: "Standups",
        path: "/standup",
        requiredPermission: "huddle:read",
      },
    ],
  },
  {
    icon: <Rss className="h-4 w-4" />,
    label: "Feed da Empresa",
    path: "/feed-global",
    requiredPermission: "feed:read",
    contador: "feed",
  },
  {
    // Metas (E22) — grupo. Gate no sub-recurso da PÁGINA (padrão documentos_*),
    // não na operação: quem só lê precisa ver o item; quem cadastra tem
    // `metas:create` além. O cascade mostra o grupo a quem enxerga ALGUM
    // filho — o admin do sistema chega em "Conexões" mesmo sem
    // `metas_listagem:view`.
    icon: <Target className="h-4 w-4" />,
    label: "Metas",
    path: "/metas",
    requiredPermission: "metas_listagem:view",
    children: [
      {
        icon: <Plus className="h-3 w-3" />,
        label: "Cadastrar",
        path: "/metas/cadastro",
        requiredPermission: "metas_cadastro:view",
      },
      {
        icon: <Package className="h-3 w-3" />,
        label: "Produtos",
        path: "/metas/produtos",
        requiredPermission: "metas_produtos:view",
      },
      {
        icon: <Upload className="h-3 w-3" />,
        label: "Importar planilha",
        path: "/metas/importar",
        requiredPermission: "metas:import",
      },
      {
        icon: <CloudUpload className="h-3 w-3" />,
        label: "Publicação no DW",
        path: "/metas/sync",
        requiredPermission: "metas_sync:view",
      },
      {
        icon: <Link2 className="h-3 w-3" />,
        label: "Mapeamento supervisor",
        path: "/metas/mapeamento",
        requiredPermission: "metas_mapeamento:view",
      },
      {
        icon: <Database className="h-3 w-3" />,
        label: "Conexões externas",
        path: "/metas/conexao",
        requiredPermission: "metas_conexao:view",
      },
    ],
  },
  {
    // Motor de Publicação — Story 3.1. PRIMEIRA porta do módulo: até aqui ele
    // só existia como API e como seção dentro da tela de grupo.
    //
    // 🔴 **Gate em `publicacao:read` desde a story 4.7** — e o parágrafo que
    // estava aqui explicava por que era `configurar` antes: até a 4.4,
    // `publicacao:read` tinha ZERO ocorrências no repositório (nada a semeava,
    // nada a consumia), e usá-la teria escondido o item de todo mundo. A 4.4
    // semeou as duas permissões da fila; a 4.7 as consome. A premissa caiu, o
    // desvio deixou de existir, e o gate voltou a ser o da arquitetura.
    //
    // ⚠️ O `path` do pai aponta para a FILA, não mais para as ocasiões. Sem
    // isso, quem tem só `publicacao:read` — o Marketing, a persona desta parte
    // do módulo — enxergava o grupo pelo cascade, clicava no rótulo e batia num
    // muro de permissão, porque o rótulo do grupo é um `<Link>` de verdade (a
    // seta de expandir é um `<button>` à parte). A fila é também a porta certa
    // por uso: mensal, contra o uso anual da configuração.
    //
    // ⚠️ Sem este item a página fica INALCANÇÁVEL mesmo com a rota registrada:
    // o teste de contrato de rotas não lê o Sidebar (medido), então removê-lo
    // deixa tudo verde. É a reincidência de "feature no ar, testada e sem
    // porta" — por isso há asserção de nível 1 sobre este item.
    //
    // 🆕 **Story 4.1 — virou GRUPO com filhos.** A regra de destino é irmã das
    // ocasiões (o UX: "configuração é irmã da fila, não filha"), então ela
    // precisa de porta própria — e um segundo item raiz chamado "Regra de
    // destino" ao lado de "Publicações" seria navegação sem dono. O grupo é
    // também a forma que sobrevive à story 4.7 acrescentar a fila.
    //
    // ⚠️ **A 4.7 trocou o destino do PAI: agora é a fila.** A intenção de 4.1
    // continua valendo — quem clicar no grupo cai numa tela real, não num nó
    // morto —, mas a tela real passou a ser a fila, porque ela é a única que
    // TODO mundo do módulo enxerga (`publicacao:read`) e a de uso mensal.
    // Apontar para ocasiões dava muro de permissão a quem só tem `read`.
    icon: <CalendarHeart className="h-4 w-4" />,
    label: "Publicações",
    path: "/publicacoes/fila",
    requiredPermission: "publicacao:read",
    children: [
      {
        icon: <ListChecks className="h-3 w-3" />,
        label: "Fila",
        path: "/publicacoes/fila",
        requiredPermission: "publicacao:read",
      },
      {
        icon: <CalendarHeart className="h-3 w-3" />,
        label: "Ocasiões",
        path: "/publicacoes/ocasioes",
        requiredPermission: "publicacao:configurar",
      },
      {
        icon: <Route className="h-3 w-3" />,
        label: "Regra de destino",
        path: "/publicacoes/regras-destino",
        requiredPermission: "publicacao:configurar",
      },
      // Story 6.3 (FR40) — a PORTA do calendário de dias não úteis. Sem esta
      // entrada a tela existiria só por URL, que é a definição de promessa não
      // cumprida: o contrato de rotas fica verde e a página, inalcançável.
      // Gate `configurar`, como as duas irmãs de configuração acima.
      {
        icon: <CalendarOff className="h-3 w-3" />,
        label: "Dias não úteis",
        path: "/publicacoes/dias-nao-uteis",
        requiredPermission: "publicacao:configurar",
      },
    ],
  },
  {
    // Central do Parceiro (E21.1.1 + E21.1.4) — grupo. O cascade mostra o grupo
    // a quem enxerga ALGUM filho; logo o publisher (central:publish) vê o grupo
    // via o filho "Gestão" mesmo sem `central:manage_operators` no pai.
    icon: <Megaphone className="h-4 w-4" />,
    label: "Central do Parceiro",
    path: "/central",
    requiredPermission: "central:manage_operators",
    children: [
      {
        icon: <PenSquare className="h-3 w-3" />,
        label: "Gestão",
        path: "/central/gestao",
        requiredPermission: "central:publish",
      },
      {
        icon: <FileText className="h-3 w-3" />,
        label: "Documentos",
        path: "/central/documentos",
        requiredPermission: "central:publish",
      },
      {
        icon: <BookOpen className="h-3 w-3" />,
        label: "Base de Conhecimento",
        path: "/central/artigos",
        requiredPermission: "central:publish",
      },
      {
        icon: <BarChart3 className="h-3 w-3" />,
        label: "Painel de Controle",
        path: "/central/painel",
        requiredPermission: "central:publish",
      },
      {
        icon: <FileCog className="h-3 w-3" />,
        label: "Templates de Remessa",
        path: "/central/templates",
        requiredPermission: "central:publish",
      },
      {
        icon: <FileOutput className="h-3 w-3" />,
        label: "Gerar Remessa",
        path: "/central/remessas",
        requiredPermission: "central:publish",
      },
      {
        icon: <Users className="h-3 w-3" />,
        label: "Operadores por Setor",
        path: "/central",
        requiredPermission: "central:manage_operators",
      },
    ],
  },
  {
    icon: <CalendarDays className="h-4 w-4" />,
    label: "Calendário",
    path: "/calendar",
    requiredPermission: "calendar:read",
  },
  {
    icon: <GitBranch className="h-4 w-4" />,
    label: "Process Designer",
    path: "/process-designer",
    // `update`, e não `read`: a base comum dá `read` a todo mundo, e o item
    // é ferramenta de quem desenha processo — a Qualidade e os admins
    // (decisão do Augusto, 25/09/2026). O endereço segue aberto a quem lê.
    requiredPermission: "process_designer:update",
  },
  {
    // A tela existia sem item no menu; quem gerencia o glossário (a Qualidade
    // e os admins) não tinha como chegar nela (25/09/2026).
    icon: <BookText className="h-4 w-4" />,
    label: "Glossário",
    path: "/ajuda/glossario/gerenciar",
    requiredPermission: "glossario:gerenciar",
  },
  {
    icon: <BookOpen className="h-4 w-4" />,
    label: "Onboarding",
    path: "/onboarding/kanban",
    requiredPermission: "onboarding:analytics:view",
    children: [
      {
        icon: <Activity className="h-3 w-3" />,
        label: "Kanban",
        path: "/onboarding/kanban",
        requiredPermission: "onboarding:analytics:view",
      },
    ],
  },
  {
    icon: <FileText className="h-4 w-4" />,
    label: "Documentos",
    path: "/documentos/dashboard",
    requiredPermission: "documentos_dashboard:view",
    children: [
      {
        icon: <LayoutDashboard className="h-3 w-3" />,
        label: "Dashboard",
        path: "/documentos/dashboard",
        requiredPermission: "documentos_dashboard:view",
      },
      {
        icon: <List className="h-3 w-3" />,
        label: "Listagem",
        path: "/documentos/listagem",
        requiredPermission: "documentos_listagem:view",
      },
      {
        icon: <HardDrive className="h-3 w-3" />,
        label: "Drive",
        path: "/drive",
        requiredPermission: "documentos_listagem:view",
      },
      {
        icon: <Trash2 className="h-3 w-3" />,
        label: "Lixeira",
        path: "/documentos/lixeira",
        requiredPermission: "documentos_lixeira:view",
      },
      {
        icon: <FileSearch className="h-3 w-3" />,
        label: "Arquivos Compartilhados",
        path: "/documentos/compartilhados",
        requiredPermission: "documents:read",
      },
    ],
  },
  {
    icon: <Workflow className="h-4 w-4" />,
    label: "Workflows",
    path: "/workflows/inbox",
    requiredPermission: "workflows:inbox_view",
    children: [
      {
        icon: <Eye className="h-3 w-3" />,
        label: "Inbox",
        path: "/workflows/inbox",
        requiredPermission: "workflows:inbox_view",
      },
      {
        icon: <Zap className="h-3 w-3" />,
        label: "Designer",
        path: "/workflows/designer",
        requiredPermission: "workflows:designer_view",
        badge: "Protótipo",
      },
      {
        icon: <ClipboardCheck className="h-3 w-3" />,
        label: "Aprovações Pendentes",
        path: "/workflows/approvals",
        requiredPermission: "workflows:inbox_view",
      },
    ],
  },
  {
    icon: <Shield className="h-4 w-4" />,
    label: "VPN",
    path: "/vpn/dashboard",
    requiredPermission: "vpn_dashboard:view",
    children: [
      {
        icon: <BarChart3 className="h-3 w-3" />,
        label: "Dashboard",
        path: "/vpn/dashboard",
        requiredPermission: "vpn_dashboard:view",
      },
      {
        icon: <ListChecks className="h-3 w-3" />,
        label: "Lista de VPNs",
        path: "/vpn/lista",
        requiredPermission: "vpn_lista:view",
      },
      {
        icon: <Plus className="h-3 w-3" />,
        label: "Cadastro",
        path: "/vpn/cadastro",
        requiredPermission: "vpn_cadastro:manage",
      },
      {
        icon: <Users className="h-3 w-3" />,
        label: "Geração em Lote",
        path: "/vpn/cadastro-lote",
        requiredPermission: "vpn_cadastro:manage",
      },
      {
        icon: <Terminal className="h-3 w-3" />,
        label: "Logs de Operações",
        path: "/vpn/logs-operacoes",
        requiredPermission: "vpn_logs_operacoes:view",
      },
      {
        icon: <Map className="h-3 w-3" />,
        label: "Matriz de Acesso (Antiga)",
        path: "/vpn/matriz",
        requiredPermission: "vpn_matriz:view",
      },
    ],
  },
  {
    icon: <Monitor className="h-4 w-4" />,
    label: "Máquinas",
    path: "/maquinas/lista",
    requiredPermission: "maquinas_lista:view",
    children: [
      {
        icon: <HardDrive className="h-3 w-3" />,
        label: "Inventário",
        path: "/maquinas/lista",
        requiredPermission: "maquinas_lista:view",
      },
      {
        icon: <BarChart3 className="h-3 w-3" />,
        label: "Dashboard Métricas",
        path: "/maquinas/dashboard",
        requiredPermission: "maquinas_dashboard:view",
      },
      {
        icon: <Package className="h-3 w-3" />,
        label: "Equipamentos",
        path: "/maquinas/equipamentos",
        requiredPermission: "maquinas_lista:view",
      },
      {
        icon: <Settings className="h-3 w-3" />,
        label: "Tipos de Equipamentos",
        path: "/maquinas/tipos",
        requiredPermission: "maquinas_tipos:manage",
      },
    ],
  },
  {
    icon: <Server className="h-4 w-4" />,
    label: "Infraestrutura",
    path: "/infraestrutura/deploy",
    requiredPermission: "infraestrutura:deploy_view",
    children: [
      {
        icon: <Zap className="h-3 w-3" />,
        label: "Deploy",
        path: "/infraestrutura/deploy",
        requiredPermission: "infraestrutura:deploy_view",
      },
      {
        icon: <Database className="h-3 w-3" />,
        label: "Backups",
        path: "/infraestrutura/backups",
        requiredPermission: "backup:view",
      },
      {
        icon: <BookOpen className="h-3 w-3" />,
        label: "Runbooks",
        path: "/infraestrutura/runbooks",
        requiredPermission: "runbook:view",
      },
      {
        icon: <BarChart3 className="h-3 w-3" />,
        label: "Dashboard Runbooks",
        path: "/infraestrutura/runbooks/dashboard",
        requiredPermission: "runbook:view",
      },
    ],
  },
  {
    icon: <Users2 className="h-4 w-4" />,
    label: "RH",
    path: "/rh/colaboradores",
    requiredPermission: "rh:colaboradores_read",
    children: [
      {
        icon: <Users className="h-3 w-3" />,
        label: "Colaboradores",
        path: "/rh/colaboradores",
        requiredPermission: "rh:colaboradores_read",
      },
      {
        icon: <BarChart3 className="h-3 w-3" />,
        label: "Dashboard Setores",
        path: "/rh/dashboard-setores",
        requiredPermission: "rh:dashboard",
      },
      {
        icon: <Building2 className="h-3 w-3" />,
        label: "Cadastro de Setores",
        path: "/rh/cadastro-setores",
        requiredPermission: "rh:setores_create",
      },
      {
        icon: <ListChecks className="h-3 w-3" />,
        label: "Logs Movimentação",
        path: "/rh/logs-movimentacao",
        requiredPermission: "rh:auditoria_view",
      },
      {
        icon: <Building2 className="h-3 w-3" />,
        label: "Cadastro Categoria Organograma",
        path: "/rh/cadastro-categoria-organograma",
        requiredPermission: "rh:setores_create",
      },
      {
        icon: <GitBranch className="h-3 w-3" />,
        label: "Editor Estrutura Hierárquica",
        path: "/rh/editor-estrutura-organograma",
        requiredPermission: "rh:organograma_edit",
      },
      {
        icon: <Eye className="h-3 w-3" />,
        label: "Visualização em Árvore",
        path: "/rh/visualizacao-organograma",
        requiredPermission: "rh:organograma_view",
      },
      {
        icon: <MapPin className="h-3 w-3" />,
        label: "Controle de Unidades",
        path: "/rh/controle-unidades",
        requiredPermission: "rh:setores_read",
      },
      {
        icon: <FileSearch className="h-3 w-3" />,
        label: "Auditoria Organograma",
        path: "/rh/auditoria-organograma",
        requiredPermission: "rh:auditoria_view",
      },
      {
        icon: <ArrowRightLeft className="h-3 w-3" />,
        label: "Migração de Setor/Unidade",
        path: "/rh/migracao-setor-unidade",
        requiredPermission: "rh:migracao_execute",
      },
      {
        icon: <LogOut className="h-3 w-3" />,
        label: "Desligamento",
        path: "/rh/desligamento",
        requiredPermission: "rh:desligamento_execute",
      },
      {
        icon: <GitBranch className="h-3 w-3" />,
        label: "Organograma",
        path: "/rh/organograma",
        requiredPermission: "rh:organograma_view",
      },
      {
        // A trava da troca da própria foto (28/09/2026): mesmo par
        // resource/action que a rota (`rh.routes.tsx`) e o backend
        // (`PUT /rh/configuracoes`) exigem.
        icon: <Settings className="h-3 w-3" />,
        label: "Configurações do RH",
        path: "/rh/configuracoes",
        requiredPermission: "rh:manage",
      },
    ],
  },
  {
    icon: <Handshake className="h-4 w-4" />,
    label: "Substabelecidos",
    path: "/substabelecidos",
    requiredPermission: "substabelecidos_lista:view",
    children: [
      {
        icon: <ListChecks className="h-3 w-3" />,
        label: "Lista",
        path: "/substabelecidos",
        requiredPermission: "substabelecidos_lista:view",
      },
      {
        icon: <PlusCircle className="h-3 w-3" />,
        label: "Novo",
        path: "/substabelecidos/novo",
        requiredPermission: "substabelecidos_cadastro:manage",
      },
      {
        // Morava no grupo Lotese, sem permissão — e mantinha o grupo visível
        // para todos. É o portal dos parceiros: fica aqui (25/09/2026).
        icon: <ExternalLink className="h-3 w-3" />,
        label: "Portal do Parceiro",
        path: "/portal/login",
        external: true,
        requiredPermission: "substabelecidos_lista:view",
      },
    ],
  },
  {
    icon: <Settings className="h-4 w-4" />,
    label: "Administração",
    path: "/admin/usuarios",
    requiredPermission: "admin:access",
    children: [
      {
        icon: <Users className="h-3 w-3" />,
        label: "Gestão de Usuários",
        path: "/admin/usuarios",
        requiredPermission: "users:manage",
      },
      {
        // Roteiro (01/10/2026). Aparece para quem tem `roteiro:ver` MESMO sem
        // `admin:access`: o pai "Administração" fica visível quando um filho está
        // liberado (o Juarez vê o grupo só com este item).
        icon: <ListChecks className="h-3 w-3" />,
        label: "Roteiro",
        path: "/admin/roteiro",
        requiredPermission: "roteiro:ver",
      },
      {
        // C1b — a entrada do menu entra JUNTO com a página. No C1 a
        // funcionalidade foi para produção inalcançável porque a validação foi
        // por URL direta e ninguém chegava nela pelo caminho natural.
        icon: <MonitorSmartphone className="h-3 w-3" />,
        label: "Telas do Portal",
        path: "/admin/portal-telas",
        requiredPermission: "substabelecidos:manage",
      },
      {
        // Opções do chat para todos (cartão do site nos links). Só aparece para quem pode mudar
        // a chave: `chat_config:manage`, a mesma do `PUT /chat/configuracoes`.
        icon: <MessageCircle className="h-3 w-3" />,
        label: "Chat",
        path: "/admin/chat",
        requiredPermission: "chat_config:manage",
      },
      {
        icon: <Shield className="h-3 w-3" />,
        label: "Gestão de Perfis",
        path: "/admin/perfis",
        requiredPermission: "roles:manage",
      },
      {
        icon: <Key className="h-3 w-3" />,
        label: "Gestão de Permissões",
        path: "/admin/permissoes",
        requiredPermission: "permissions:manage",
      },
      {
        icon: <Network className="h-3 w-3" />,
        label: "Raio-X do Sistema",
        path: "/admin/modules",
        requiredPermission: "modules:read",
      },
      {
        icon: <Upload className="h-3 w-3" />,
        label: "Import Wizard",
        path: "/admin/import-wizard",
        requiredPermission: "admin:access",
      },
      {
        icon: <Construction className="h-3 w-3" />,
        label: "Manutenção",
        path: "/admin/maintenance",
        requiredPermission: "maintenance:activate",
      },
      {
        icon: <Activity className="h-3 w-3" />,
        label: "Suporte (Manutenção)",
        path: "/admin/maintenance/support",
        requiredPermission: "maintenance:view",
      },
      {
        icon: <ScrollText className="h-3 w-3" />,
        label: "Histórico Manutenção",
        path: "/admin/maintenance/history",
        requiredPermission: "maintenance:view",
      },
      {
        icon: <AlertTriangle className="h-3 w-3" />,
        label: "Emergência",
        path: "/admin/maintenance/emergency",
        requiredPermission: "maintenance:activate",
      },
      {
        icon: <AlertTriangle className="h-3 w-3" />,
        label: "Erros do Sistema",
        path: "/admin/erros-sistema",
        requiredPermission: "system_logs:read",
      },
      {
        icon: <ClipboardCheck className="h-3 w-3" />,
        label: "Análise IA de Erros",
        path: "/admin/erros-sistema/ia",
        requiredPermission: "system_logs:read",
      },
      {
        icon: <Cog className="h-3 w-3" />,
        label: "Providers de IA",
        path: "/admin/ai-providers",
        requiredPermission: "system_logs:read",
      },
      {
        icon: <HardDrive className="h-3 w-3" />,
        label: "Storage",
        path: "/admin/storage",
        requiredPermission: "admin:storage:manage",
      },
      {
        // Credenciais AWS da biometria (spec 2026-09-23). O rótulo casa com a
        // mensagem do 503 do backend — "Administração › Biometria › Credenciais
        // AWS" —, que manda a pessoa exatamente para cá. Mesma permissão da
        // rota e do backend: só Administrator.
        icon: <ScanFace className="h-3 w-3" />,
        label: "Biometria › Credenciais AWS",
        path: "/admin/biometria/credenciais-aws",
        requiredPermission: "biometria:configurar",
      },
      {
        icon: <Zap className="h-3 w-3" />,
        label: "Event Bus",
        path: "/admin/event-bus",
        requiredPermission: "admin:event_bus:read",
      },
      {
        icon: <ShieldAlert className="h-3 w-3" />,
        label: "LGPD",
        path: "/admin/lgpd",
        requiredPermission: "lgpd:dpo",
      },
      {
        // Audit Log Unificado (Expansão v2 / E4.2.2). Permissão ``audit:read``
        // já seedada em ``audit_permissions_seed.py`` (E4.2.1). Tem `audit:export`
        // separada — botão de export só aparece pra quem tem a perm.
        icon: <ShieldCheck className="h-3 w-3" />,
        label: "Audit Log",
        path: "/admin/audit-events",
        requiredPermission: "audit:read",
      },
      {
        // RBAC Audit (Expansão v2 / E5.1.3). Permissão ``admin:rbac:view``
        // seedada em ``admin_permissions_seed.py`` (E5.1.1). Read-only —
        // CRUD continua em /admin/perfis e /admin/permissoes.
        icon: <KeyRound className="h-3 w-3" />,
        label: "RBAC Audit",
        path: "/admin/rbac-audit",
        requiredPermission: "admin:rbac:view",
      },
      {
        // Widgets da Home (E19 Fase 5). Configura quais widgets cada perfil
        // RBAC vê na tela inicial + ordem. Permissão ``home_widgets:manage``
        // seedada em ``home_widgets_permissions_seed.py``.
        icon: <LayoutDashboard className="h-3 w-3" />,
        label: "Widgets da Home",
        path: "/admin/home-widgets",
        requiredPermission: "home_widgets:manage",
      },
      {
        // Mapa de Acesso (E1 Fase B). A regra setor -> papéis de módulo, e a
        // tela onde as movimentações de setor são decididas. Permissão
        // ``mapa_acesso:manage`` seedada em ``mapa_acesso_permissions_seed.py``.
        icon: <Network className="h-3 w-3" />,
        label: "Mapa de Acesso",
        path: "/admin/mapa-de-acesso",
        requiredPermission: "mapa_acesso:manage",
      },
      {
        icon: <ArrowLeftRight className="h-3 w-3" />,
        label: "Migração Bitrix24",
        path: "/admin/migration/bitrix-import",
        requiredPermission: "migration:execute",
      },
      {
        icon: <Activity className="h-3 w-3" />,
        label: "Cutover Bitrix",
        path: "/admin/migration/cutover-status",
        requiredPermission: "migration:execute",
      },
      {
        icon: <KeyRound className="h-3 w-3" />,
        label: "Split-key recordings",
        path: "/admin/split-key",
        requiredPermission: "split_key:read",
      },
      {
        icon: <Store className="h-3 w-3" />,
        label: "Marketplace de templates",
        path: "/admin/marketplace",
        requiredPermission: "marketplace:read",
      },
      {
        icon: <Cog className="h-3 w-3" />,
        label: "Automações",
        path: "/admin/automations",
        requiredPermission: "notifications:rules:manage",
      },
      {
        icon: <Activity className="h-3 w-3" />,
        label: "Sentiment Dashboard",
        path: "/admin/sentiment-dashboard",
        requiredPermission: "admin:access",
      },
      {
        icon: <MessageCircle className="h-3 w-3" />,
        label: "WhatsApp Passthrough",
        path: "/admin/whatsapp-passthrough",
        requiredPermission: "admin:access",
      },
      {
        icon: <Megaphone className="h-3 w-3" />,
        label: "Broadcasts Interativos",
        path: "/admin/interactive-broadcasts",
        requiredPermission: "interactive_broadcasts:read",
      },
      {
        icon: <Settings className="h-3 w-3" />,
        label: "Branding (Tenant)",
        path: "/admin/branding",
        requiredPermission: "admin:branding:manage",
      },
      {
        icon: <Video className="h-3 w-3" />,
        label: "Áudio/Vídeo Infra",
        path: "/admin/audio-video/infrastructure",
        requiredPermission: "audio_video_admin:read",
      },
      {
        icon: <Film className="h-3 w-3" />,
        label: "Gravações",
        path: "/admin/recordings",
        requiredPermission: "calls:read",
      },
      {
        icon: <Monitor className="h-3 w-3" />,
        label: "Modo Apresentação (TV)",
        path: "/admin/apresentacao",
        requiredPermission: "admin:access",
      },
      {
        icon: <ShieldAlert className="h-3 w-3" />,
        label: "Anti-Fraude",
        path: "/admin/anti-fraud",
        requiredPermission: "admin:access",
      },
      {
        icon: <UsersRound className="h-3 w-3" />,
        label: "Partner Onboarding",
        path: "/admin/partner-onboarding",
        requiredPermission: "admin:access",
      },
    ],
  },
  {
    icon: <Bell className="h-4 w-4" />,
    label: "Notificações",
    path: "/notificacoes",
    children: [
      {
        icon: <Inbox className="h-3 w-3" />,
        label: "Inbox",
        path: "/notificacoes/inbox",
      },
      {
        icon: <ScrollText className="h-3 w-3" />,
        label: "Histórico",
        path: "/notificacoes/historico",
        requiredPermission: "notifications:log:view",
      },
      {
        icon: <BarChart2 className="h-3 w-3" />,
        label: "Dashboard",
        path: "/notificacoes/dashboard",
        requiredPermission: "notifications:dashboard:view",
      },
      {
        // Story E1.7.5 — Manager Dashboard com scope filter (lojas/setores).
        // Admin vê tenant inteiro; gestor escolhe entre setores que administra.
        icon: <BarChart2 className="h-3 w-3" />,
        label: "Dashboard Gestor",
        path: "/notificacoes/gestor-dashboard",
        requiredPermission: "notifications:dashboard:view",
      },
      {
        icon: <FileEdit className="h-3 w-3" />,
        label: "Templates",
        path: "/notificacoes/templates",
        requiredPermission: "notifications:templates:manage",
      },
      {
        icon: <Cog className="h-3 w-3" />,
        label: "Regras",
        path: "/notificacoes/regras",
        requiredPermission: "notifications:rules:manage",
      },
      {
        icon: <Mail className="h-3 w-3" />,
        label: "Provedores",
        path: "/notificacoes/provedores",
        requiredPermission: "notifications:providers:manage",
      },
      {
        icon: <SlidersHorizontal className="h-3 w-3" />,
        label: "Preferências",
        path: "/notifications/preferences",
        // Story E1.6.3 — self-service: todo user com BASE_USER seed
        // tem `manage_own` (lotese_granular_permissions_seed.py).
        requiredPermission: "notifications:preferences:manage_own",
      },
      {
        icon: <AlertTriangle className="h-3 w-3" />,
        label: "Dead-Letter Queue",
        path: "/notificacoes/dlq",
        requiredPermission: "notifications:admin",
      },
      {
        // Story E1.7.2 — UI única que substitui 6 sub-rotas v1.
        icon: <Settings className="h-3 w-3" />,
        label: "Centro Administrativo",
        path: "/admin/notifications",
        requiredPermission: "notifications:providers:manage",
      },
    ],
  },
  {
    icon: <Store className="h-4 w-4" />,
    label: "Lojas",
    path: "/lojas",
    requiredPermission: "lojas_lista:view",
    children: [
      {
        icon: <LayoutDashboard className="h-3 w-3" />,
        label: "Dashboard Lojas",
        path: "/lojas",
        // Os endpoints do painel exigem `lojas_gestao:view`; com
        // `lojas_lista:view` (a base comum) 125 pessoas viam a página vazia.
        requiredPermission: "lojas_gestao:view",
      },
      {
        icon: <MapPin className="h-3 w-3" />,
        label: "Lista de Lojas",
        path: "/lojas/lista",
        requiredPermission: "lojas_lista:view",
      },
      {
        icon: <Plus className="h-3 w-3" />,
        label: "Nova Loja",
        path: "/lojas/cadastro",
        requiredPermission: "lojas_cadastro:manage",
      },
      {
        icon: <MapPin className="h-3 w-3" />,
        label: "Gestão de Lojas (Antiga)",
        path: "/lojas/gestao",
        requiredPermission: "lojas_gestao:view",
      },
    ],
  },
  {
    icon: <DoorOpen className="h-4 w-4" />,
    label: "Salas",
    path: "/salas",
    requiredPermission: "rooms:read",
  },
  {
    icon: <Activity className="h-4 w-4" />,
    label: "Sinistros",
    path: "/sinistros/gestao",
    requiredPermission: "sinistro:gerenciar",
    children: [
      {
        icon: <AlertTriangle className="h-3 w-3" />,
        label: "Gestão de Sinistros",
        path: "/sinistros/gestao",
        requiredPermission: "sinistro:gerenciar",
      },
    ],
  },
  {
    icon: <Code2 className="h-4 w-4" />,
    label: "APIs",
    path: "/apis",
    requiredPermission: "apis:view",
    children: [
      {
        icon: <LayoutDashboard className="h-3 w-3" />,
        label: "Dashboard",
        path: "/apis",
        requiredPermission: "apis:view",
      },
      {
        icon: <Key className="h-3 w-3" />,
        label: "Tokens de Acesso",
        path: "/apis/tokens",
        requiredPermission: "api_tokens:view",
      },
      {
        icon: <FileCode className="h-3 w-3" />,
        label: "Catálogo de APIs",
        path: "/apis/catalog",
        requiredPermission: "api_catalog:view",
      },
      {
        icon: <ScrollText className="h-3 w-3" />,
        label: "Logs de Requisições",
        path: "/apis/logs",
        requiredPermission: "api_logs:view",
      },
    ],
  },
  {
    icon: <Webhook className="h-4 w-4" />,
    label: "Webhooks",
    path: "/webhooks",
    requiredPermission: "webhooks:read",
    children: [
      {
        icon: <ListChecks className="h-3 w-3" />,
        label: "Lista de Webhooks",
        path: "/webhooks",
        requiredPermission: "webhooks:read",
      },
      {
        icon: <PlusCircle className="h-3 w-3" />,
        label: "Novo Webhook",
        path: "/webhooks/new",
        requiredPermission: "webhooks:create",
      },
      {
        icon: <ScrollText className="h-3 w-3" />,
        label: "Logs de Eventos",
        path: "/apis/webhooks",
        requiredPermission: "webhooks:logs_view",
      },
    ],
  },
  {
    icon: <Lock className="h-4 w-4" />,
    label: "Segurança",
    path: "/seguranca/dashboard-auditoria",
    requiredPermission: "security:read",
    children: [
      {
        icon: <LayoutDashboard className="h-3 w-3" />,
        label: "Dashboard Auditoria",
        path: "/seguranca/dashboard-auditoria",
        requiredPermission: "security:read",
      },
      {
        icon: <Activity className="h-3 w-3" />,
        label: "Logs de Atividades",
        path: "/seguranca/logs-atividades",
        requiredPermission: "security:read",
      },
      {
        icon: <Shield className="h-3 w-3" />,
        label: "Logs de Segurança",
        path: "/seguranca/logs-seguranca",
        requiredPermission: "security:read",
      },
      {
        icon: <Terminal className="h-3 w-3" />,
        label: "Exportação Logs (SIEM)",
        path: "/seguranca/exportacao-logs",
        requiredPermission: "security:export",
      },
      {
        icon: <Eye className="h-3 w-3" />,
        label: "Auditoria de Acesso",
        path: "/seguranca/auditoria-acesso",
        requiredPermission: "security:audit",
      },
      {
        icon: <FileSearch className="h-3 w-3" />,
        label: "Auditoria Substabelecidos",
        path: "/seguranca/auditoria-substabelecidos",
        requiredPermission: "security:audit",
      },
      // Gerenciamento de Perfis / Cadastro de Papéis / Edição de Permissões
      // migraram para /admin/perfis e /admin/permissoes (FASE 2 — dedup com
      // Admin). As rotas antigas redirecionam; o item não aparece mais aqui.
      // "Delegação Temporária" saiu daqui em 11/09/2026. A tela existia, era
      // alcançável por quem tem `permissions:assign` (o papel Administrator,
      // 2 pessoas) e escrevia "Delegação criada com sucesso!" — gravando a
      // linha SÓ no estado do React. Atualizar a página apagava, e nenhuma
      // permissão mudava. Ver DelegacaoPermissoes.tsx:1 para a decisão.
      {
        icon: <GitBranch className="h-3 w-3" />,
        label: "Hierarquia de Perfis",
        path: "/seguranca/hierarquia-perfis",
        requiredPermission: "roles:manage",
      },
      {
        icon: <Key className="h-3 w-3" />,
        label: "MFA SSO",
        path: "/seguranca/mfa-sso",
        requiredPermission: "security:manage",
      },
      {
        icon: <Settings className="h-3 w-3" />,
        label: "Parâmetros Gerais",
        path: "/seguranca/parametros-gerais",
        requiredPermission: "settings:manage",
      },
      {
        icon: <Settings className="h-3 w-3" />,
        label: "Configurações Técnicas Avançadas",
        path: "/seguranca/configuracoes-tecnicas-avancadas",
        requiredPermission: "settings:advanced",
      },
    ],
  },
  {
    // O requiredPermission do item pai é só pra esconder o grupo inteiro
    // quando o user não tem NENHUMA subpágina liberada. Usamos
    // lotese_dashboard:view porque qualquer role Lotese tem pelo menos ela.
    icon: <CreditCard className="h-4 w-4" />,
    label: "Lotese",
    path: "/lotese",
    requiredPermission: "lotese_dashboard:view",
    children: [
      {
        icon: <LayoutDashboard className="h-3 w-3" />,
        label: "Dashboard",
        path: "/lotese",
        requiredPermission: "lotese_dashboard:view",
      },
      {
        icon: <FileText className="h-3 w-3" />,
        label: "Comprovantes",
        path: "/lotese/comprovantes",
        requiredPermission: "lotese_comprovantes:view",
      },
      {
        icon: <Database className="h-3 w-3" />,
        label: "Extratos",
        path: "/lotese/extratos",
        requiredPermission: "lotese_extratos:view",
      },
      {
        icon: <ClipboardCheck className="h-3 w-3" />,
        label: "Conferência",
        path: "/lotese/conferencia",
        requiredPermission: "lotese_conferencia:view",
      },
      {
        icon: <ArrowRightLeft className="h-3 w-3" />,
        label: "Conciliação",
        path: "/lotese/conciliacao",
        requiredPermission: "lotese_conciliacao:view",
      },
      {
        icon: <Zap className="h-3 w-3" />,
        label: "Baixas GES",
        path: "/lotese/baixas",
        requiredPermission: "lotese_baixas:view",
      },
      {
        icon: <GitCompareArrows className="h-3 w-3" />,
        label: "Coletas externas",
        path: "/lotese/coletas-externas",
        requiredPermission: "lotese_baixas:view",
      },
      {
        icon: <MapPin className="h-3 w-3" />,
        label: "Pontos GES",
        path: "/lotese/pontos",
        requiredPermission: "lotese_pontos:view",
      },
      {
        icon: <FileBarChart className="h-3 w-3" />,
        label: "Relatórios",
        path: "/lotese/relatorios",
        requiredPermission: "lotese_relatorios:view",
      },
      {
        icon: <Settings className="h-3 w-3" />,
        label: "Configurações",
        path: "/lotese/configuracoes",
        requiredPermission: "lotese_config:view",
      },
      {
        icon: <Upload className="h-3 w-3" />,
        label: "Formulario Publico",
        path: "/public/lotese/comprovante",
        external: true,
        // Sem permissão, este subitem sozinho fazia o grupo Lotese aparecer
        // para as 159 pessoas (medido em 25/09/2026).
        requiredPermission: "lotese_dashboard:view",
      },
    ],
  },
  {
    icon: <Palette className="h-4 w-4" />,
    label: "Aparência",
    path: "/aparencia",
  },
  {
    icon: <Ticket className="h-4 w-4" />,
    label: "Chamados",
    path: "/tickets",
    requiredPermission: "tickets:read",
  },
  {
    icon: <Megaphone className="h-4 w-4" />,
    label: "Atendimentos (Helpdesk)",
    path: "/atendimentos",
    requiredPermission: "atendimentos:read",
    children: [
      {
        // 25/09/2026 — o painel mora na aba "Atendimento externo" de
        // /mensagens. Direto para lá, sem passar pelo atalho de /atendimentos.
        icon: <Activity className="h-3 w-3" />,
        label: "Painel ao vivo",
        path: "/mensagens?aba=atendimento",
        requiredPermission: "atendimentos:read",
      },
      {
        icon: <Phone className="h-3 w-3" />,
        label: "Fila de callbacks",
        path: "/atendimentos/callbacks",
        requiredPermission: "atendimentos:read",
      },
      {
        icon: <TrendingUp className="h-3 w-3" />,
        label: "NPS",
        path: "/atendimentos/nps",
        requiredPermission: "atendimentos:read",
      },
      {
        icon: <ShieldCheck className="h-3 w-3" />,
        label: "Auditorias",
        path: "/atendimentos/auditorias",
        requiredPermission: "atendimentos:read",
      },
      {
        // Story E9.1.4 — SLA & Métricas dashboard.
        icon: <BarChart3 className="h-3 w-3" />,
        label: "SLA & Métricas",
        path: "/atendimentos/sla-metrics",
        requiredPermission: "atendimentos:read",
      },
      {
        // Story E9.2.1 — Admin de Templates de resposta + Tags.
        icon: <FileText className="h-3 w-3" />,
        label: "Templates & Tags",
        path: "/atendimentos/templates",
        requiredPermission: "atendimento_templates:read",
      },
      {
        // Story E9.2.3 — Bot Triagem (config + menu YAML viewer).
        icon: <Bot className="h-3 w-3" />,
        label: "Bot Triagem",
        path: "/atendimentos/bot",
        requiredPermission: "atendimentos:bot_read",
      },
      {
        // Alavanca da gravação. Entra no MENU junto com a página: tela sem
        // porta de entrada é tela que ninguém encontra.
        icon: <Video className="h-3 w-3" />,
        label: "Gravação",
        path: "/atendimentos/gravacao",
        requiredPermission: "calls:supervisor",
      },
    ],
  },
  {
    icon: <KanbanSquare className="h-4 w-4" />,
    label: "Tarefas",
    path: "/tasks",
    requiredPermission: "tasks:read",
  },
  {
    icon: <FolderKanban className="h-4 w-4" />,
    label: "Projetos",
    path: "/projects",
    requiredPermission: "projects:read",
    children: [
      {
        icon: <FolderKanban className="h-3 w-3" />,
        label: "Projetos",
        path: "/projects",
        requiredPermission: "projects:read",
      },
      {
        // Time Tracking Dashboard agregado (Story E11.2.4) — gate tasks:read.
        icon: <BarChart3 className="h-3 w-3" />,
        label: "Dashboard de Horas",
        path: "/projects/time-tracking",
        requiredPermission: "tasks:read",
      },
    ],
  },
  {
    icon: <KeyRound className="h-4 w-4" />,
    label: "Cofre Técnico",
    path: "/cofre",
    requiredPermission: "licenses:read",
    children: [
      {
        icon: <BarChart3 className="h-3 w-3" />,
        label: "Dashboard",
        path: "/cofre",
        requiredPermission: "licenses:read",
      },
      {
        icon: <Lock className="h-3 w-3" />,
        label: "Credenciais",
        path: "/cofre/credenciais",
        requiredPermission: "credentials:read",
      },
      {
        icon: <KeyRound className="h-3 w-3" />,
        label: "Licenças",
        path: "/cofre/licencas",
        requiredPermission: "licenses:read",
      },
      {
        icon: <Layers className="h-3 w-3" />,
        label: "Lotes",
        path: "/cofre/lotes",
        requiredPermission: "licenses:read",
      },
      {
        icon: <CreditCard className="h-3 w-3" />,
        label: "Contas de Compra",
        path: "/cofre/contas-de-compra",
        requiredPermission: "purchase_accounts:read",
      },
      {
        icon: <Building2 className="h-3 w-3" />,
        label: "Fornecedores",
        path: "/cofre/fornecedores",
        requiredPermission: "suppliers:read",
      },
      {
        icon: <Users2 className="h-3 w-3" />,
        label: "Off-Boarding",
        path: "/cofre/off-boarding",
        requiredPermission: "credentials:update",
      },
    ],
  },
  {
    icon: <Building2 className="h-4 w-4" />,
    label: "Perfil da Filial",
    path: "/filial",
    requiredPermission: "lojas:read",
  },
];

// Sidebar resize / collapse constants — desktop only
const SIDEBAR_MIN_WIDTH = 64; // ícones-only
const SIDEBAR_DEFAULT_WIDTH = 256;
const SIDEBAR_MAX_VW_RATIO = 0.3; // 30% da viewport
const COLLAPSE_THRESHOLD = 90; // px abaixo disso o drag auto-colapsa

const getMaxSidebarWidth = () =>
  Math.max(SIDEBAR_MIN_WIDTH + 1, Math.floor(window.innerWidth * SIDEBAR_MAX_VW_RATIO));

/** "1 mensagem não lida" / "3 mensagens não lidas" — o mesmo texto do `UnreadBadge`. */
const fraseDeNaoLidas = (total: number): string =>
  `${total} ${total === 1 ? "mensagem não lida" : "mensagens não lidas"}`;

/** "1 post novo" / "3 posts novos" — o número do item "Feed da Empresa". */
const fraseDePostsNovos = (total: number): string =>
  `${total} ${total === 1 ? "post novo" : "posts novos"}`;

const Sidebar = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  // Mobile: showOnMobile alterna entre overlay aberto e fechado.
  // Desktop: sidebar fica sempre visível — `isCollapsed` apenas alterna ícones-only.
  const [showOnMobile, setShowOnMobile] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem("sidebar-collapsed") === "true";
    } catch {
      return false;
    }
  });
  const [sidebarWidth, setSidebarWidth] = useState<number>(() => {
    try {
      const saved = localStorage.getItem("sidebar-width");
      const n = saved ? parseInt(saved, 10) : NaN;
      if (Number.isFinite(n) && n >= SIDEBAR_MIN_WIDTH) return n;
    } catch {
      /* ignore */
    }
    return SIDEBAR_DEFAULT_WIDTH;
  });
  const [isDragging, setIsDragging] = useState(false);
  const [openSections, setOpenSections] = useState<Set<string>>(new Set());
  const [userPermissions, setUserPermissions] = useState<Set<string>>(new Set());
  const [isLoadingPermissions, setIsLoadingPermissions] = useState(true);
  const location = useLocation();
  const { isAuthenticated, user } = useAuth();
  const { isModuleAffected, scope, isActive: isMaintenanceActive } = useMaintenanceContext();

  // Load user permissions on mount
  useEffect(() => {
    const loadPermissions = async () => {
      console.log("[Sidebar] Loading permissions - Auth:", isAuthenticated, "User:", user);

      // A API retorna user_id (UserResponse não tem `id`)
      const userId = user?.user_id;

      if (!isAuthenticated || !user || !userId) {
        console.log("[Sidebar] Skipping permissions load - not authenticated or no user ID");
        setUserPermissions(new Set());
        setIsLoadingPermissions(false);
        return;
      }

      console.log("[Sidebar] Fetching permissions for user:", userId);
      try {
        const response = await rbacService.getUserPermissions(userId);
        console.log("[Sidebar] Permissions loaded:", response.permissions.length, "permissions");
        console.log("[Sidebar] First 10 permissions:", response.permissions.slice(0, 10));
        // Permissions come as strings in "resource:action" format (ver docs/RBAC-CONVENTION.md)
        setUserPermissions(parsePermissionsFromAPI(response.permissions));
      } catch (error) {
        console.error("[Sidebar] Failed to load permissions:", error);
        setUserPermissions(new Set());
      } finally {
        setIsLoadingPermissions(false);
      }
    };

    loadPermissions();
  }, [isAuthenticated, user]);

  // Check if user has required permission (delegates to @/lib/permissions — Story E5.1.2)
  const hasPermission = (requiredPermission?: string): boolean => {
    if (!requiredPermission) return true;
    return checkPermission(requiredPermission, userPermissions);
  };

  // O número de não lidas do chat: UMA chamada para o menu inteiro (os itens e o botão "Menu" só leem).
  // Sem `chat:read` não busca (D2, passo 14).
  const naoLidasDoChat = useNaoLidasDoChat({ ativo: hasPermission("chat:read") });

  // O número de posts novos do feed: também UMA chamada para o menu inteiro. Sem `feed:read` não busca nem abre
  // socket; na página do Feed o hook devolve zero e fecha o dele (a timeline tem o seu). `meuId` é o mesmo
  // `user_id` que o `useFeedTimeline` usa para reconhecer "o meu" (é o que vem em `post.author_id`).
  const novidadesDoFeed = useNovidadesDoFeed({
    ativo: hasPermission("feed:read"),
    meuId: user?.user_id ?? null,
    naPaginaDoFeed: location.pathname === "/feed-global",
  });

  // Recursively filter sidebar items based on permissions
  const filterItemsByPermission = (items: SidebarItem[]): SidebarItem[] => {
    return items
      .map((item): SidebarItem | null => {
        // Filter children first (if any)
        const filteredChildren = item.children ? filterItemsByPermission(item.children) : undefined;

        // Keep item if:
        // 1. User has permission for this item, OR
        // 2. Item has no permission requirement (public), OR
        // 3. Item has filtered children (parent should show if children are visible)
        const shouldShow =
          hasPermission(item.requiredPermission) ||
          (filteredChildren && filteredChildren.length > 0);

        if (!shouldShow) return null;

        return {
          ...item,
          children: filteredChildren,
        };
      })
      .filter((item): item is SidebarItem => item !== null);
  };

  /**
   * O item corresponde à URL atual?
   *
   * Desde 25/09/2026 um item pode ter query (`/mensagens?aba=atendimento`).
   * Comparar o `pathname` com o `path` inteiro nunca casaria esse item. O
   * que vale: mesmo caminho E os parâmetros do item presentes na URL. Item
   * sem query casa só pelo caminho, como sempre.
   */
  const rotaCasa = (path: string): boolean => {
    const [base, query] = path.split("?");
    // Decode pathname to handle special characters (é, ã, etc.)
    if (decodeURIComponent(location.pathname) !== base) return false;
    if (!query) return true;
    const atuais = new URLSearchParams(location.search);
    return [...new URLSearchParams(query)].every(([chave, valor]) => atuais.get(chave) === valor);
  };

  // Função para verificar se um módulo contém a rota atual
  const isModuleActive = (item: SidebarItem): boolean => {
    // Verifica se a rota atual é a do próprio item
    if (rotaCasa(item.path)) return true;

    // Verifica se algum filho contém a rota atual
    if (item.children) {
      return item.children.some(
        (child) => rotaCasa(child.path) || (child.children && isModuleActive(child))
      );
    }

    return false;
  };

  // Get filtered sidebar items based on permissions (memoized to avoid unnecessary re-calculations)
  const filteredSidebarItems = useMemo(() => {
    if (isLoadingPermissions) {
      return []; // Don't show items while loading permissions
    }
    return filterItemsByPermission(sidebarItems);
  }, [isLoadingPermissions, userPermissions]);

  // Debug log
  useEffect(() => {
    console.log("[Sidebar] Filtered items count:", filteredSidebarItems.length);
    console.log("[Sidebar] User permissions count:", userPermissions.size);
    console.log("[Sidebar] Is loading:", isLoadingPermissions);
  }, [filteredSidebarItems, userPermissions, isLoadingPermissions]);

  // Auto-expandir seção do módulo ativo quando a rota muda
  useEffect(() => {
    setOpenSections((prevOpenSections) => {
      const newOpenSections = new Set(prevOpenSections);
      let hasChanges = false;

      filteredSidebarItems.forEach((item) => {
        if (item.children && isModuleActive(item)) {
          if (!newOpenSections.has(item.path)) {
            newOpenSections.add(item.path);
            hasChanges = true;
          }
        }
      });

      return hasChanges ? newOpenSections : prevOpenSections;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search]); // Só a URL (`?aba=` conta, ver `rotaCasa`): evita loops

  // Detectar se é mobile com melhor performance
  useEffect(() => {
    const checkMobile = () => {
      const mobileBreakpoint = 768;
      const isCurrentlyMobile = window.innerWidth < mobileBreakpoint;
      setIsMobile(isCurrentlyMobile);
    };

    checkMobile();

    // Otimizar evento de resize para evitar múltiplas chamadas
    let resizeTimeout: NodeJS.Timeout;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(checkMobile, 100);
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      clearTimeout(resizeTimeout);
    };
  }, []);

  // Compat — mantém o nome usado em alguns handlers.
  // Mobile: alterna overlay. Desktop: alterna ícones-only (collapsed).
  const toggleSidebar = () => {
    if (isMobile) {
      setShowOnMobile((v) => !v);
      return;
    }
    setIsCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem("sidebar-collapsed", String(next));
      } catch {
        /* ignore */
      }
      return next;
    });
  };

  // Drag handlers (desktop only) — adiciona listeners globais enquanto drag ativo.
  useEffect(() => {
    if (!isDragging || isMobile) return;

    const onMove = (e: MouseEvent) => {
      const max = getMaxSidebarWidth();
      const next = Math.min(Math.max(e.clientX, SIDEBAR_MIN_WIDTH), max);
      setSidebarWidth(next);
      // Auto-colapsa quando o usuário arrasta abaixo do threshold.
      if (next <= COLLAPSE_THRESHOLD) {
        setIsCollapsed(true);
      } else if (isCollapsed) {
        setIsCollapsed(false);
      }
    };

    const onUp = () => {
      setIsDragging(false);
      try {
        // Persiste somente width "real" (>= default) — colapso é flag separada.
        localStorage.setItem(
          "sidebar-width",
          String(Math.max(sidebarWidth, SIDEBAR_DEFAULT_WIDTH))
        );
        localStorage.setItem("sidebar-collapsed", String(isCollapsed));
      } catch {
        /* ignore */
      }
    };

    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    return () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
  }, [isDragging, isMobile, isCollapsed, sidebarWidth]);

  // Quando viewport muda, reaperta a clamp pra não ficar maior que 30vw.
  useEffect(() => {
    const onResize = () => {
      const max = getMaxSidebarWidth();
      setSidebarWidth((w) => (w > max ? max : w));
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const toggleSection = (path: string) => {
    const newOpenSections = new Set(openSections);
    if (newOpenSections.has(path)) {
      newOpenSections.delete(path);
    } else {
      newOpenSections.add(path);
    }
    setOpenSections(newOpenSections);
  };

  const getPaddingClass = (level: number) => {
    // Classes Tailwind predefinidas para diferentes níveis com responsividade
    const paddingClasses = [
      "pl-3 sm:pl-4", // Nível 0
      "pl-6 sm:pl-8", // Nível 1
      "pl-9 sm:pl-12", // Nível 2
      "pl-12 sm:pl-16", // Nível 3
    ];
    return paddingClasses[level] || "pl-3 sm:pl-4";
  };

  const renderSidebarItem = (item: SidebarItem, level = 0) => {
    const isActive = rotaCasa(item.path);
    const hasChildren = item.children && item.children.length > 0;
    const isSectionOpen = openSections.has(item.path);
    const paddingClass = getPaddingClass(level);
    const moduleActive = level === 0 && isModuleActive(item);
    // Em modo ícones-only (desktop colapsado), só renderiza top-level
    // — labels, chevron e children ficam ocultos. Mobile não usa esse modo.
    const iconsOnly = !isMobile && isCollapsed;
    if (iconsOnly && level > 0) return null;

    // Maintenance indicator for top-level module items
    const pathPrefix = "/" + item.path.split("/")[1];
    const moduleIdForMaint = level === 0 ? PATH_TO_MODULE_ID[pathPrefix] : undefined;
    const isModuleInMaintenance =
      moduleIdForMaint &&
      isMaintenanceActive &&
      scope === "partial" &&
      isModuleAffected(moduleIdForMaint);

    // Menu recolhido: o rótulo-botão do pai sem permissão própria não existe (só o ícone), então o ícone leva ao
    // primeiro filho que a pessoa pode abrir, não ao muro de permissão de `item.path`. Com a permissão do pai,
    // nada muda.
    const destinoDoLink =
      iconsOnly && hasChildren && level === 0 && !hasPermission(item.requiredPermission)
        ? (item.children?.find((filho) => hasPermission(filho.requiredPermission))?.path ??
          item.path)
        : item.path;

    // Número do chat: selo à direita do rótulo; recolhido não cabe, vira um ponto sobre o ícone e o número
    // vai no `title`/`aria-label` do link (o ponto é só cor, não fala). O Feed segue a mesma ideia com os
    // posts novos, mas sem o vermelho: a menção é do chat.
    const contadorDoChat = item.contador === "chat";
    const contadorDoFeed = item.contador === "feed";
    const pontoDoChat = iconsOnly && contadorDoChat && naoLidasDoChat.total > 0;
    const pontoDoFeed = iconsOnly && contadorDoFeed && novidadesDoFeed.total > 0;
    const complementoRecolhido = pontoDoChat
      ? ` (${fraseDeNaoLidas(naoLidasDoChat.total)}${naoLidasDoChat.temMencao ? ", com menção" : ""})`
      : pontoDoFeed
        ? ` (${fraseDePostsNovos(novidadesDoFeed.total)})`
        : "";
    const seloDoChat = contadorDoChat ? (
      <UnreadBadge
        count={naoLidasDoChat.total}
        hasMentions={naoLidasDoChat.temMencao}
        className="shrink-0"
      />
    ) : null;
    const seloDoFeed = contadorDoFeed ? (
      <UnreadBadge
        count={novidadesDoFeed.total}
        rotulo={fraseDePostsNovos(novidadesDoFeed.total)}
        className="shrink-0"
      />
    ) : null;

    return (
      <div key={item.path} className="space-y-1">
        <div
          className={`flex items-center relative ${
            moduleActive
              ? "before:absolute before:left-0 before:top-0 before:bottom-0 before:w-1 before:bg-primary before:rounded-r-full"
              : ""
          }`}
        >
          {item.external ? (
            <a
              href={item.path}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`Abrir ${item.label} em nova aba`}
              title={iconsOnly ? item.label : undefined}
              className={`flex-1 ${iconsOnly ? "justify-center" : "justify-start"} ${
                iconsOnly ? "" : paddingClass
              } hover:bg-muted rounded-md flex items-center gap-2 sm:gap-3 py-2 px-2 sm:px-3 text-xs sm:text-sm font-medium transition-colors min-h-[44px] text-muted-foreground hover:text-foreground`}
            >
              <span>{item.icon}</span>
              {!iconsOnly && (
                <>
                  <span className="flex-1 text-left truncate">{item.label}</span>
                  <ExternalLink className="h-3 w-3 opacity-50" />
                </>
              )}
            </a>
          ) : hasChildren &&
            level === 0 &&
            !iconsOnly &&
            !hasPermission(item.requiredPermission) ? (
            // Sem a permissão do próprio pai (ex.: quem só tem `roteiro:ver` vê
            // "Administração" por causa de um filho): navegar para `item.path`
            // cairia em /unauthorized. O rótulo abre e fecha o grupo.
            <button
              type="button"
              onClick={() => toggleSection(item.path)}
              aria-expanded={isSectionOpen}
              className={`flex-1 ${paddingClass} hover:bg-muted rounded-md flex items-center gap-2 sm:gap-3 py-2 px-2 sm:px-3 text-xs sm:text-sm font-medium transition-colors min-h-[44px] ${
                isActive
                  ? "bg-accent text-accent-foreground"
                  : moduleActive
                    ? "bg-primary/5 text-primary font-semibold"
                    : "text-muted-foreground hover:text-foreground"
              }`}
            >
              <span className={moduleActive ? "text-primary" : ""}>{item.icon}</span>
              <span className="flex-1 text-left truncate">{item.label}</span>
              {isModuleInMaintenance && (
                <span
                  className="inline-flex items-center text-maintenance-warn"
                  aria-label="Módulo em manutenção"
                  title="Em manutenção"
                >
                  <Construction className="h-4 w-4" />
                </span>
              )}
              {seloDoChat}
              {seloDoFeed}
              {item.badge && (
                <span className="inline-flex items-center rounded-md bg-yellow-100 px-1.5 py-0.5 text-[10px] font-medium text-yellow-800 whitespace-nowrap">
                  {item.badge}
                </span>
              )}
            </button>
          ) : (
            <Link
              to={destinoDoLink}
              aria-label={`Navegar para ${item.label}${complementoRecolhido}`}
              title={iconsOnly ? `${item.label}${complementoRecolhido}` : undefined}
              // `min-w-0`: sem ele o mínimo do link (item de flex) é o rótulo INTEIRO (`truncate` traz `nowrap`) e o
              // filho "Mensagens / Chat" passava 13 px da borda do menu a 1366, cortando o selo. Com ele o rótulo
              // trunca e o selo (`shrink-0`) fica dentro. Medido no E2E de 07/10.
              className={`flex-1 min-w-0 ${iconsOnly ? "justify-center" : "justify-start"} ${
                iconsOnly ? "" : paddingClass
              } hover:bg-muted rounded-md flex items-center gap-2 sm:gap-3 py-2 px-2 sm:px-3 text-xs sm:text-sm font-medium transition-colors min-h-[44px] ${
                isActive
                  ? "bg-accent text-accent-foreground"
                  : moduleActive
                    ? "bg-primary/5 text-primary font-semibold"
                    : "text-muted-foreground hover:text-foreground"
              }`}
            >
              <span
                className={`${moduleActive ? "text-primary" : ""}${pontoDoChat || pontoDoFeed ? " relative" : ""}`}
              >
                {item.icon}
                {pontoDoChat && (
                  <span
                    aria-hidden="true"
                    data-testid="contador-chat-ponto"
                    className={`absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full ${
                      naoLidasDoChat.temMencao ? "bg-destructive" : "bg-primary"
                    }`}
                  />
                )}
                {pontoDoFeed && (
                  <span
                    aria-hidden="true"
                    data-testid="contador-feed-ponto"
                    className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-primary"
                  />
                )}
              </span>
              {!iconsOnly && (
                <>
                  <span className="flex-1 text-left truncate">{item.label}</span>
                  {isModuleInMaintenance && (
                    <span
                      className="inline-flex items-center text-maintenance-warn"
                      aria-label="Módulo em manutenção"
                      title="Em manutenção"
                    >
                      <Construction className="h-4 w-4" />
                    </span>
                  )}
                  {seloDoChat}
                  {seloDoFeed}
                  {item.badge && (
                    <span className="inline-flex items-center rounded-md bg-yellow-100 px-1.5 py-0.5 text-[10px] font-medium text-yellow-800 whitespace-nowrap">
                      {item.badge}
                    </span>
                  )}
                </>
              )}
            </Link>
          )}
          {hasChildren && !iconsOnly && (
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                toggleSection(item.path);
              }}
              className={`p-1 rounded hover:bg-muted min-h-[32px] min-w-[32px] flex items-center justify-center transition-colors ${
                moduleActive ? "text-primary" : "text-muted-foreground hover:text-foreground"
              }`}
              aria-label={`Expandir ${item.label}`}
            >
              {isSectionOpen ? (
                <ChevronDown className="h-3 w-3 sm:h-4 sm:w-4" />
              ) : (
                <ChevronRight className="h-3 w-3 sm:h-4 sm:w-4" />
              )}
            </button>
          )}
        </div>

        {hasChildren && isSectionOpen && !iconsOnly && (
          <div
            className={`${
              level === 0 ? "ml-6 sm:ml-8" : level === 1 ? "ml-9 sm:ml-12" : "ml-12 sm:ml-16"
            } space-y-1`}
          >
            {item.children?.map((child) => renderSidebarItem(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  // Cálculo de visibilidade / largura
  const showSidebar = isMobile ? showOnMobile : true;
  const desktopWidthPx = isCollapsed
    ? SIDEBAR_MIN_WIDTH
    : Math.min(Math.max(sidebarWidth, SIDEBAR_MIN_WIDTH), getMaxSidebarWidth());
  const iconsOnlyMode = !isMobile && isCollapsed;

  // Botão "Menu" do celular: o selo SOMA mensagens do chat e posts novos; o texto de leitor de tela diz os dois
  // separados ("2 mensagens não lidas, 3 posts novos"). A menção é do chat e vai logo depois das mensagens.
  const fraseDoBotaoMenu = [
    naoLidasDoChat.total > 0
      ? `${fraseDeNaoLidas(naoLidasDoChat.total)}${naoLidasDoChat.temMencao ? ", com menção" : ""}`
      : null,
    novidadesDoFeed.total > 0 ? fraseDePostsNovos(novidadesDoFeed.total) : null,
  ]
    .filter(Boolean)
    .join(", ");

  return (
    <>
      {/* Botão flutuante para abrir o menu — só mobile, quando o overlay está
          fechado.

          Reportado pelo Augusto (05/08/2026): "fica muito lá em cima, na parte
          esquerda, e fica pequenininho demais; não fica intuitivo".

          Três mudanças, uma para cada queixa:

          1. TAMANHO — era 44×44px com ícone de 20px. 44px é o MÍNIMO recomendado
             para alvo de toque, não um tamanho confortável; passou a 56×56 com
             ícone de 24px.
          2. POSIÇÃO — encaixado DENTRO da faixa do header (61px), medido no
             navegador: 52px de altura a 4px do topo ocupa y=4..56 e sobra
             folga. A 1a tentativa usou piso de 1.25rem, copiando o raciocínio
             da câmera, e o botão vazou 15px para FORA do header, parando a 3px
             do título da página. O contexto é outro: a câmera é fullscreen (o
             portal cobre a barra de status, então o inset é indispensável), o
             menu está em fluxo normal de página — ali o navegador já começa o
             viewport abaixo da barra de status. O inset entra só como soma,
             para o caso de landscape com entalhe.
          3. INTUITIVIDADE — o ícone sozinho não diz o que faz. Ganhou o rótulo
             "Menu", que é o conserto direto para "não fica intuitivo". */}
      {isMobile && !showOnMobile && (
        <button
          onClick={toggleSidebar}
          className="fixed z-50 flex items-center gap-2 rounded-xl bg-primary px-4 text-primary-foreground shadow-lg ring-1 ring-black/10 transition-colors hover:bg-primary/90 active:scale-95"
          style={{
            top: "calc(0.25rem + env(safe-area-inset-top, 0px))",
            left: "max(1rem, env(safe-area-inset-left, 0px))",
            minHeight: "52px",
            minWidth: "52px",
          }}
          aria-label={fraseDoBotaoMenu ? `Abrir menu (${fraseDoBotaoMenu})` : "Abrir menu"}
          aria-expanded={false}
          data-testid="menu-mobile-abrir"
        >
          <Menu className="h-6 w-6" />
          <span className="text-sm font-medium">Menu</span>
          {/* O botão já é `fixed`, que ancora o selo; um `relative` aqui venceria o `fixed` na ordem do CSS.
              `-top-0.5` e não `-top-1.5`: o botão fica a 4 px do topo (sem entalhe), e -6 px jogava o selo em
              y = -2 (cortado, sem o anel de cima). Com -2 px o selo fica em y = 2 e o `ring-2` em 0. */}
          <UnreadBadge
            count={naoLidasDoChat.total + novidadesDoFeed.total}
            hasMentions={naoLidasDoChat.total > 0 && naoLidasDoChat.temMencao}
            rotulo={novidadesDoFeed.total > 0 ? fraseDoBotaoMenu : undefined}
            className="absolute -top-0.5 -right-1.5 ring-2 ring-background"
          />
        </button>
      )}

      {/* Sidebar Overlay para mobile */}
      {isMobile && showOnMobile && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-30"
          onClick={toggleSidebar}
          role="button"
          tabIndex={0}
          aria-label="Fechar menu lateral"
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") toggleSidebar();
          }}
        />
      )}

      {/* Sidebar */}
      <div
        style={
          !isMobile ? { width: `${desktopWidthPx}px`, minWidth: `${desktopWidthPx}px` } : undefined
        }
        className={`glass border-r h-screen flex flex-col relative ${
          isDragging ? "" : "transition-[width,min-width] duration-300"
        } ${
          isMobile
            ? showSidebar
              ? "fixed inset-y-0 left-0 z-40 w-full max-w-xs shadow-xl"
              : "hidden"
            : ""
        }`}
      >
        {/* Header */}
        <div
          className={`flex items-center ${
            iconsOnlyMode ? "justify-center" : "justify-between"
          } p-3 sm:p-4 border-b`}
        >
          <div
            className={`flex items-center gap-2 sm:gap-3 ${iconsOnlyMode ? "" : "flex-1"} min-w-0`}
          >
            <img
              src="/logo.png"
              alt="SE Promotora"
              className="w-7 h-7 sm:w-8 sm:h-8 object-contain rounded-lg flex-shrink-0"
              onError={(e) => {
                // Fallback p/ ícone caso o asset esteja indisponível
                const img = e.target as HTMLImageElement;
                img.style.display = "none";
                const fallback = img.nextElementSibling as HTMLElement | null;
                if (fallback) fallback.style.display = "flex";
              }}
            />
            <div className="w-7 h-7 sm:w-8 sm:h-8 bg-primary rounded-lg items-center justify-center flex-shrink-0 hidden">
              <Shield className="h-3 w-3 sm:h-4 sm:w-4 text-primary-foreground" />
            </div>
            {!iconsOnlyMode && (
              <div className="min-w-0 flex-1">
                <h1 className="text-sm sm:text-lg font-semibold truncate">SeWeb</h1>
                <p className="text-xs text-muted-foreground truncate">Plataforma Integrada</p>
              </div>
            )}
          </div>
          {!iconsOnlyMode && (
            <button
              onClick={toggleSidebar}
              className="p-1 rounded-lg hover:bg-muted min-h-[32px] min-w-[32px] flex items-center justify-center flex-shrink-0 transition-colors"
              aria-label={isMobile ? "Fechar menu" : "Recolher menu"}
              title={isMobile ? "Fechar menu" : "Recolher menu"}
            >
              <SidebarClose className="h-4 w-4" />
            </button>
          )}
        </div>
        {/* Botão de expansão visível APENAS no modo colapsado (desktop) */}
        {iconsOnlyMode && (
          <div className="px-2 pb-2">
            <button
              onClick={toggleSidebar}
              className="w-full p-1 rounded-lg hover:bg-muted min-h-[32px] flex items-center justify-center transition-colors text-muted-foreground"
              aria-label="Expandir menu"
              title="Expandir menu"
            >
              <SidebarOpen className="h-4 w-4" />
            </button>
          </div>
        )}

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-2 sm:py-4 space-y-1 sm:space-y-2">
          {isLoadingPermissions ? (
            <div className="flex items-center justify-center p-8">
              <div className="text-center space-y-2">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                <p className="text-sm text-muted-foreground">Carregando menu...</p>
              </div>
            </div>
          ) : (
            filteredSidebarItems.map((item) => renderSidebarItem(item))
          )}
        </nav>

        {/* Footer — oculto no modo ícones-only */}
        {!iconsOnlyMode && (
          <div className="p-3 sm:p-4 border-t mt-auto">
            <div className="text-xs text-muted-foreground space-y-1">
              <p className="truncate">Versão 1.0.0</p>
              <p className="truncate">© {new Date().getFullYear()} Se Promotora</p>
            </div>
          </div>
        )}

        {/* Drag handle — desktop only, na borda direita */}
        {!isMobile && (
          <div
            role="separator"
            aria-orientation="vertical"
            aria-label="Redimensionar menu (arraste para esquerda/direita)"
            onMouseDown={(e) => {
              e.preventDefault();
              setIsDragging(true);
            }}
            onDoubleClick={() => {
              // Reset rápido pra largura padrão
              setSidebarWidth(SIDEBAR_DEFAULT_WIDTH);
              setIsCollapsed(false);
              try {
                localStorage.setItem("sidebar-width", String(SIDEBAR_DEFAULT_WIDTH));
                localStorage.setItem("sidebar-collapsed", "false");
              } catch {
                /* ignore */
              }
            }}
            className={`absolute top-0 right-0 h-full w-1.5 cursor-col-resize z-20 transition-colors ${
              isDragging ? "bg-primary/40" : "hover:bg-primary/20"
            }`}
          />
        )}
      </div>
    </>
  );
};

export default Sidebar;
